import { forwardRef, useState } from 'react';
import NotificationsIcon from '@mui/icons-material/Notifications';
import { abortController } from '../utils/abortController';
import { ButtonGroup, IconButton, Button, CircularProgress, FormControl, RadioGroup, FormControlLabel, Radio } from '@mui/material';
import Modal from 'react-bootstrap/Modal';
import axios from 'axios';
import useUser from '../hooks/useUser';

const DocumentReadyNotice = forwardRef(({
    selectedBusinessUnit = null,
    selectedPFI = null,
    selectedTranche = null,
    isDisabled = false,
    setIsLoading = () => { },
    setShowAlert = () => { },
    setAlertMessage = () => { },
    setAlertType = () => { },
    triggerSessionExpire = () => { },
    logOut = () => { },
    toggleDocumentList = false,
    setToggleDocumentList = () => { },
    handleResetModal = () => { }
}, ref) => {
    const currentUser = useUser();
    const [showNotifyModal, setShowNotifyModal] = useState(false);
    const [isNotifying, setIsNotifying] = useState(false);
    const [notificationStatus, setNotificationStatus] = useState('created');

    const handleNotifyClick = () => {
        setShowNotifyModal(true);
    };

    const handleCloseModal = () => {
        setShowNotifyModal(false);
        handleResetModal();
    };

    const handleStatusChange = (event) => {
        setNotificationStatus(event.target.value);
    };

    const handleConfirmNotify = async () => {
        handleCloseModal();
        setIsNotifying(true);
        setIsLoading(true);
        setShowAlert(false);

        try {
            const response = await axios({
                method: 'post',
                url: '/api/PostShipmentDocumentNotification',
                data: {
                    Business_Unit_Id: selectedBusinessUnit,
                    user_id: currentUser?.id,
                    deal_pfi_id: selectedPFI,
                    tranche_name: selectedTranche?.value,
                    status: notificationStatus
                },
                signal: abortController.signal
            });

            if (response.status === 200) {
                setAlertMessage('Notification sent successfully to all stakeholders.');
                setAlertType('success');
                setToggleDocumentList(!toggleDocumentList);
            } else {
                setAlertMessage(response.data?.message || 'Failed to send notification. Please try again.');
                setAlertType('warning');
            }
        } catch (error) {
            console.error('Notification API error:', error);

            if (axios.isCancel(error) || error.code === "ERR_CANCELED") {
                return;
            }

            const status = error?.response?.status;
            if (status === 403) {
                triggerSessionExpire();
            } else {
                setAlertMessage(
                    status === 401
                        ? 'Unauthorized access.'
                        : status === 429
                            ? 'Request limit exceeded. Your account is temporarily disabled.'
                            : error.response?.data?.message || 'An error occurred while sending notification.'
                );
                setAlertType('error');

                if (status === 429) {
                    setTimeout(logOut, 3000);
                }
            }
        } finally {
            setShowAlert(true);
            setIsNotifying(false);
            setIsLoading(false);
            handleCloseModal();
        }
    };

    return (
        <div className="position-relative">
            <ButtonGroup variant="contained">
                <Button sx={{ cursor: 'default !important', width: '170px' }}>Notify Stakeholders</Button>
                <IconButton
                    color="primary"
                    disabled={isDisabled || !selectedTranche}
                    onClick={handleNotifyClick}
                    sx={{ margin: '5px !important' }}
                >
                    {isNotifying ? (
                        <CircularProgress color="inherit" />
                    ) : (
                        <NotificationsIcon fontSize="small" />
                    )}
                </IconButton>
            </ButtonGroup>

            <Modal
                show={showNotifyModal}
                onHide={handleCloseModal}
                className="custom-modal"
                size="md"
                centered
            >
                <Modal.Header style={{padding: '7px 16px'}} closeButton>
                    <Modal.Title>Confirm Notification</Modal.Title>
                </Modal.Header>
                <Modal.Body>

                    <span>
                        Do you want to notify regarding the Post-Shipment documents to all Trade Partners, Trade Partner Supervisors, Document Team and LC Team?
                    </span>
                    <FormControl sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 2 }}>
                        <span>Notification Type:</span>
                        <RadioGroup
                            row
                            value={notificationStatus}
                            onChange={handleStatusChange}
                            name="notification-status"
                            sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 2 }}
                        >
                            <FormControlLabel value="created" control={<Radio />} label="Create" />
                            <FormControlLabel value="updated" control={<Radio />} label="Update" />
                        </RadioGroup>
                    </FormControl>
                </Modal.Body>
                <Modal.Footer className="gap-2">
                    <Button variant="outlined" size="small" onClick={handleCloseModal}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        size="small"
                        onClick={handleConfirmNotify}
                        disabled={isNotifying}
                    >
                        {isNotifying ? (
                            <>
                                <CircularProgress size={20} color="inherit" /> Sending...
                            </>
                        ) : (
                            'Confirm'
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
});

export default DocumentReadyNotice;